import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { Queries } from '@shared/api';
import { notNil } from '@shared/util/notNil';
import { useCurrentBusinessId } from './useCurrentBusinessId';

/**
 * A hook that uses a cache-only policy to retrieve channel list items.
 */
export function useChannelsFromCache(): MBAPI.ChannelListItem[] {
  const businessId = useCurrentBusinessId();
  const { data } = useQuery<MBAPI.ChannelListQuery, MBAPI.ChannelListQueryVariables>(
    Queries.channel.list,
    {
      variables: { businessId: businessId ?? -1 },
      skip: isNil(businessId),
      fetchPolicy: 'cache-only',
    },
  );

  return useMemo(
    () => data?.me?.business?.channels?.filter(notNil) ?? [],
    [data?.me?.business?.channels],
  );
}

interface UseChannelFromCacheProps {
  channelSlug?: string;
  channelId?: number;
}

export function useChannelFromCache({
  channelSlug,
  channelId,
}: UseChannelFromCacheProps): MBAPI.ChannelListItem | undefined {
  const channels = useChannelsFromCache();
  if (channelSlug) {
    return channels.find(channel => channel.slug === channelSlug);
  }
  if (channelId) {
    return channels.find(channel => channel.id === channelId);
  }
  return undefined;
}
